/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, FocusEvent, memo} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {Account} from '@ion/core/src/types/entities';
import {
  FormItem
} from '@ion/dls/src/components/forms/FormItem';
import {
  FormSection
} from '@ion/dls/src/components/forms/FormSection';
import {
  FormTextField
} from '@ion/dls/src/components/forms/FormTextField';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {SSOFormFields} from './SSOEditorUtil';
import {validateSSODomain} from './SSOValidator';

export interface SSOInfoProps {
  handleFieldChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  handleChooserChange?: Function;
  selectedAccount: Account;
  formState?: object;
  dialogTitle?: string;
  disabled?: boolean;
};

/**
 * The SSOInfo component provides the UI which allows to add/edit
 *  fields such as their Discovery End Point, Domains, and SSO Mode.
 *
 * ```typescript
 *
 *    import {SSOInfo} from '@ion/applications/src/pages/admin/accounts/SSOInfo';
 *
 *    <SSOInfo handleChooserChange={handleChooserChange}
 *      handleFieldChange={handleFieldChange}
 *      selectedAccount={selectedAccount}
 *      formState={formState} />
 *
 * ```
 */
export const SSOInfo = ({
  handleChooserChange,
  handleFieldChange,
  formState,
  onBlur,
  dialogTitle = '',
  disabled = false
}: SSOInfoProps) => {
  return (
    <FormSection bottomBorder title={dialogTitle}>
      <>
        <FormItem>
          <FormTextField required
            fieldLabel={translate('admin-groups:ssoConfig.discoveryEndpoint.label')}
            placeholder={translate('admin-groups:ssoConfig.discoveryEndpoint.placeholder')}
            name={SSOFormFields.DiscoveryEndpoint}
            onChange={handleFieldChange}
            disabled={disabled}
            hasError={formState[SSOFormFields.DiscoveryEndpoint]?.error}
            errorText={formState[SSOFormFields.DiscoveryEndpoint]?.value === ''
              ? translate('common:editors.shared.required')
              : translate('admin-groups:ssoConfig.discoveryEndpoint.error')}
            defaultValue={formState[SSOFormFields.DiscoveryEndpoint]?.value}
            maxLength={2048} />
        </FormItem>
        <FormItem>
          <FormTextField required
            fieldLabel={translate('admin-groups:ssoConfig.domains.label')}
            placeholder={translate('admin-groups:ssoConfig.domains.placeholder')}
            name={SSOFormFields.Domains}
            onChange={handleFieldChange}
            onBlur={onBlur}
            disabled={disabled}
            hasError={formState[SSOFormFields.Domains]?.error}
            errorText={validateSSODomain(formState[SSOFormFields.Domains]?.value)}
            defaultValue={formState[SSOFormFields.Domains]?.value}
            maxLength={2048} />
        </FormItem>
        <FormItem>
          <Dropdown options={translate('admin-groups:ssoModeOptions', {returnObjects: true})}
            fieldLabel={translate('admin-groups:ssoConfig.ssoMode.label')}
            placeholder={translate('admin-groups:ssoConfig.ssoMode.placeholder')}
            id={SSOFormFields.SSOMode}
            disabled={disabled}
            hasError={formState[SSOFormFields.SSOMode]?.error}
            value={formState[SSOFormFields.SSOMode]?.value || ''}
            onChange={function({target: {value}}, child) {
              handleChooserChange({
                name  : SSOFormFields.SSOMode,
                value : child?.value || value
              });
            }} />
        </FormItem>
      </>
    </FormSection>
  );
};

export default memo(SSOInfo);
